/**
 * @name Header
 * @description Encabezado del datagrid con columnas, checkbox y ordenamiento
 * @author Ricardo Guerra
 */

import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled, { css } from 'styled-components';

// Material-UI
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiTableSortLabel from '@material-ui/core/TableSortLabel';

import { Icons, MUI } from '@styles';

const Header = (props) => {
  const {
    columns,
    rowsLength,
    selectedRowsLength,
    canSelect,
    canSelectAll,
    canSort,
    order,
    orderBy,
    onSelectAll,
    onRequestSort,
    showHeader
  } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <Styled.TableHead showHeader={showHeader}>
      <Styled.TableRow>
        {/* Checkbox */}
        {canSelect && (
          <Styled.TableCell padding="checkbox">
            {canSelectAll && (
              // eslint-disable-next-line no-nested-ternary
              <MUI.Tooltip title={(selectedRowsLength === 0) ? i18n.t('GenericMessages.selectAll') : (selectedRowsLength === rowsLength) ? i18n.t('GenericMessages.unselectAll') : i18n.t('GenericMessages.unselectSelected')}>
                <MUI.Checkbox
                  indeterminate={selectedRowsLength > 0 && selectedRowsLength < rowsLength}
                  checked={rowsLength > 0 && selectedRowsLength === rowsLength}
                  onChange={onSelectAll}
                />
              </MUI.Tooltip>
            )}
          </Styled.TableCell>
        )}
        {/* Columnas */}
        {columns.map((column) => {
          return (
            column.visible && (
              <Styled.TableCell
                key={column.id}
                align={column.align}
                style={{ width: column.width, minWidth: column.minWidth }}
                sortDirection={orderBy === column.id ? order : false}
                showHeader={showHeader}
              >
                {/* Ordenamiento */}
                <Styled.TableSortLabel
                  activesort={Boolean(canSort && column.sort).toString()}
                  active={(canSort && column.sort) ? orderBy === column.id : false}
                  direction={orderBy === column.id ? order : 'asc'}
                  hideSortIcon={(!canSort || !column.sort)}
                  onClick={(canSort && column.sort) ? createSortHandler(column.id) : null}
                  align={column.align}
                  IconComponent={Icons.ArrowUp}
                >
                  {showHeader === false ? '' : column.label}
                </Styled.TableSortLabel>
              </Styled.TableCell>
            )
          );
        })}
      </Styled.TableRow>
    </Styled.TableHead>
  );
};

Header.propTypes = {
  // Colección de columnas a mostrar en el grid. 
  columns: PropTypes.array.isRequired,
  // Longitud de registros.
  rowsLength: PropTypes.number.isRequired,
  // Número de registros seleccionados.
  selectedRowsLength: PropTypes.number.isRequired,
  // Boolean que indica si se muestran los checkbox para seleccionar elementos.
  canSelect: PropTypes.bool,
  // Boolean que indica si se muestra el checkbox para seleccionar todos los elementos.
  canSelectAll: PropTypes.bool,
  // Boolean que indica si se puede ordenar las columnas.
  canSort: PropTypes.bool,
  // Indica si el ordanimiento es de forma ascendente o descendente.
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  // Indica por que valor se va a ordenar.
  orderBy: PropTypes.string.isRequired,
  // Función a ejecutar cuando se desea ordenar los elementos.
  onRequestSort: PropTypes.func.isRequired,
  // Función a ejecutar pasa seleccionar todos los registros.
  onSelectAll: PropTypes.func.isRequired,
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

// TableHead
Styled.TableHead = styled(MuiTableHead)`
  &.MuiTableHead-root {
    min-height: 60px;
    border-top: ${(props) => {
    return props.showHeader === false ? 'none' : `1px solid ${props.theme.colors.greyLight}`;
  }};
  }
`;

// TableRow 
Styled.TableRow = styled(MuiTableRow)`
  &.MuiTableRow-root {
    transition: .5s all;
  }

  &.MuiTableRow-root.MuiTableRow-hover:hover {
    background-color: ${(props) => props.theme.colors.greyLight};
    cursor: pointer;
  }
`;

// TableCell
Styled.TableCell = styled(MuiTableCell)`
  &.MuiTableCell-root {
    padding: 16px;
    font-family: 'Bryant Pro Regular';
    font-variant: tabular-nums;
    font-size: ${(props) => props.theme.typography.bodyText};
    font-weight: normal;
    line-height: 1.3;
    letter-spacing: 0.0175em;
    vertical-align: middle;
    color: ${(props) => props.theme.colors.greyDark};
    border-bottom: 1px solid ${(props) => props.theme.colors.greyLight};
    ${(props) => (props.showHeader === false) && HideColumnStyle};
  }

  &.MuiTableCell-paddingCheckbox {
    padding: 4px;
  }
`;

// TableSortLabel
Styled.TableSortLabel = styled(MuiTableSortLabel)`
  font-family: 'Bryant Pro Medium';
  font-variant: tabular-nums;
  font-weight: normal;
  width: 100%;
  justify-content: ${(props) => props.align ? props.align : 'inherit'};

  &.MuiTableSortLabel-root, 
  &.MuiTableSortLabel-root.MuiTableSortLabel-active,
  &.MuiTableSortLabel-root.MuiTableSortLabel-active.MuiTableSortLabel-root.MuiTableSortLabel-active .MuiTableSortLabel-icon {
    color: ${(props) => props.theme.colors.greyDark};
    &:hover {
      cursor: ${(props) => !props.activesort ? 'default' : 'pointer'}
    }
  }
  
  .MuiTableSortLabel-icon {
    font-size: 20px;
  }

  .MuiTableSortLabel-iconDirectionDesc, 
  .MuiTableSortLabel-iconDirectionAsc {
    transition: .5s all;
  }
`;

// Not Show Header Style
const HideColumnStyle = css`
  height: 0px !important;
  padding: 0px !important;
  line-height: 0 !important;
  border-bottom: 0px !important;
`;

export default Header;